import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useEntityStore } from '@/data/useEntityStore'
import type { Field } from '@/modules/Project/Fields/types'
import { getMilisecondsFromUUIDv7 } from '@/shared/utils'
import { isFileField } from '@/shared/utils/typeGuards'
import { canPreviewFileField } from './Fields/utils/file'
import { useProject, type Entity } from './useProject'

/**
 * Holds entities ordered by order they appear in project, which is order of insertion.
 *
 * The entity's id is a uuid v7, which means it acts as a sortable "timestamp".
 * This is what's used for ordering.
 *
 * This store does not hold full entity data,
 * only ordered ids for lookup in the global entity store.
 */
export const useEntity = defineStore('entity', () => {
  const projectStore = useProject()
  const entityId = ref<string>()
  const entityIds = ref<string[]>([])
  const areEntitiesStale = ref(false)

  const setEntityId = (id?: string) => {
    entityId.value = id
  }

  const setEntitiesStale = (stale: boolean) => (areEntitiesStale.value = stale)

  const globalEntityStore = useEntityStore()

  const entityIdx = computed<number>(() => entityIds.value.findIndex((id) => id === entityId.value))
  const entity = computed<Entity | undefined>(() =>
    entityId.value ? globalEntityStore.entitiesById[entityId.value] : undefined,
  )

  const prevId = computed<string | undefined>(() => entityIds.value[entityIdx.value - 1])
  const prev = computed<Entity | undefined>(() =>
    prevId.value ? globalEntityStore.entitiesById[prevId.value] : undefined,
  )
  const nextId = computed<string | undefined>(() => entityIds.value[entityIdx.value + 1])
  const next = computed<Entity | undefined>(() =>
    nextId.value ? globalEntityStore.entitiesById[nextId.value] : undefined,
  )

  const insertIds = (ids: string[], entityId: string, after: boolean) => {
    const index = entityIds.value.findIndex((id) => id === entityId)
    if (index === -1) return
    entityIds.value.splice(index + (after ? 1 : 0), 0, ...ids)
  }

  const insertPrevIds = (ids: string[], entityId: string) => insertIds(ids, entityId, false)
  const insertNextIds = (ids: string[], entityId: string) => insertIds(ids, entityId, true)

  const resetEntities = () => {
    entityIds.value = []
    areEntitiesStale.value = false
  }

  /**
   * Inserts or removes given entity's id into the correct position in the entityIds array,
   * based on the UUIDv7 timestamp and whether it's part of the current view or not.
   */
  const updateEntity = (updatedEntity: Entity) => {
    if (!entityId.value) return

    const isPartOfTheCurrentView = updatedEntity.activeViewIds?.includes(
      projectStore.activeView?.id ?? '',
    )

    const currentIndex = entityIds.value.findIndex((e) => e === updatedEntity.id)
    const alreadyPresent = currentIndex > -1

    // Remove entity if it's not part of the current view
    if (!isPartOfTheCurrentView) {
      if (alreadyPresent) {
        // If the current entity is being removed, select prev or next
        if (entityId.value === updatedEntity.id) {
          const prevOrNextId = prev.value?.id ?? next.value?.id
          if (prevOrNextId) setEntityId(prevOrNextId)
        }
        entityIds.value.splice(currentIndex, 1)
      }
      return
    }

    // If already present and part of the view, nothing to do (ID is immutable)
    if (alreadyPresent) return

    // Insert new entity in the correct position based on UUIDv7 timestamp
    const updatedTime = getMilisecondsFromUUIDv7(updatedEntity.id)
    const currentTime = getMilisecondsFromUUIDv7(entityId.value)

    // Insert at end if it's newer than all and no next entity
    if (!nextId.value && updatedTime > currentTime) {
      entityIds.value.push(updatedEntity.id)
      return
    }

    // Insert at start if it's older than all and no prev entity
    if (!prevId.value && updatedTime < currentTime) {
      entityIds.value.unshift(updatedEntity.id)
      return
    }

    // Insert between two loaded entities
    // Find the correct position to insert
    const sortedIds = entityIds.value
      .concat(updatedEntity.id)
      .toSorted((a, b) => getMilisecondsFromUUIDv7(a) - getMilisecondsFromUUIDv7(b))
    const insertIndex = sortedIds.findIndex((e) => e === updatedEntity.id)

    // Find neighbors in the original array
    const leftNeighbor = sortedIds[insertIndex - 1]
    const rightNeighbor = sortedIds[insertIndex + 1]
    const rightNeighborIndex = entityIds.value.findIndex((e) => e === rightNeighbor)

    // Only insert if both neighbors exist and are adjacent
    if (
      leftNeighbor &&
      rightNeighbor &&
      rightNeighborIndex > 0 &&
      entityIds.value[rightNeighborIndex - 1] === leftNeighbor
    ) {
      entityIds.value.splice(rightNeighborIndex, 0, updatedEntity.id)
    }
  }

  const removeEntities = (ids: string[]) => {
    entityIds.value = entityIds.value.filter((id) => !ids.includes(id))
  }

  /**
   * Matches entity for the given field and updates the field value for the entity.
   */
  const updateField = (field: Field) => {
    const entity = globalEntityStore.getEntity(field.entityId)
    if (!entity) return
    entity.fields.set(field.propertyId, field)
  }

  /**
   * In some views, we give special prominence to a specific field (e.g.
   * when previewing a file from the entity sidebar). This state is used
   * to keep track of the currently active property.
   */
  const activePropertyId = ref<string | null>(null)

  const fileFields = computed<Field<'file' | 'file_collection'>[]>(() =>
    Array.from(entity.value?.fields.values() || []).filter(
      // Only show file fields that can be previewed from within Go
      (f): f is Field<'file' | 'file_collection'> => isFileField(f) && canPreviewFileField(f),
    ),
  )

  return {
    areEntitiesStale,
    updateEntity,
    entity,
    entityId,
    entityIds,
    insertNextIds,
    insertPrevIds,
    next,
    nextId,
    prev,
    prevId,
    removeEntities,
    resetEntities,
    setEntitiesStale,
    setEntityId,
    updateField,
    activePropertyId,
    fileFields,
  }
})
